import { useMemo } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { useQuery } from '@tanstack/react-query';

import { permissionsSelector } from '../../../selectors/permissions.selector';
import { flagService } from '../../../services/flagService';
import { getOnboardingV3Status, getOnboardingV3StatusKey } from '../onboardingV3.service';

type OnboardingStatus = 'off' | 'in_progress' | 'completed';

// hook so that changes from flag to query are easier to change
export const useHasAccessToOnboardingV3 = () => {
  // re-using this flag just to enable quick revert and I don't want to add another
  const onboardingWizardV3UsingApi = useAppSelector((state) =>
    flagService.isFlagOn(state, 'enableOnboardingV3WizardConfig')
  );
  const orgId = useAppSelector((state) => state.currentApp?.OrgId || '');
  const isOnboardingWizardAutoSaveEnabled = useAppSelector((state) =>
    flagService.isFlagOn(state, 'enableOnboardingV3WizardAutoSave')
  );

  const { data } = useQuery({
    queryKey: [orgId, getOnboardingV3StatusKey],
    queryFn: () => getOnboardingV3Status(orgId),
    enabled: !!orgId && onboardingWizardV3UsingApi,
  });

  let onboardingStatus: OnboardingStatus = 'off';

  switch (data?.data?.status) {
    case 'in_progress':
      onboardingStatus = 'in_progress';
      break;
    case 'completed':
      onboardingStatus = 'completed';
      break;
  }

  const isFlipdishStaffSelector = useMemo(
    () => permissionsSelector.hasPermissionFactory(['FlipdishStaff']),
    []
  );
  const isFlipdishStaff = useAppSelector((state) => isFlipdishStaffSelector(state));

  // ViewOnboarding (Owner / Managed Owner); not the Owner role string.
  const hasOnboardingWizardPermissions = useAppSelector((state) =>
    state.permissions.includes('ViewOnboarding')
  );

  const permissionsLoaded = useAppSelector((state) => state.permissions.length > 0);

  // Staff + users with ViewOnboarding. Menu `permissions` are OR'd with FlipdishStaff.
  const canShowOnboardingWizardModule = isFlipdishStaff || hasOnboardingWizardPermissions;

  // Menu item for owner/staff when onboarding is on (in progress or completed); hidden when off.
  const shouldShowFirstTimeSetup = canShowOnboardingWizardModule && onboardingStatus !== 'off';

  // Shared base for in-progress chrome lock (owners and teammates; not staff).
  // Wait for permissions so empty [] is not treated as "no wizard perms" (false waiting flash).
  // Kept distinct from layout's `isRestrictedOnboardingNav` (reduced || waiting — same value).
  const isOnboardingInProgress =
    permissionsLoaded && onboardingStatus === 'in_progress' && !isFlipdishStaff;

  // Owner (wizard perms): lock into guided setup. Teammate: wait on Home.
  const reducedOnboardingView = isOnboardingInProgress && hasOnboardingWizardPermissions;
  const onboardingWaitingView = isOnboardingInProgress && !hasOnboardingWizardPermissions;

  return {
    canShowOnboardingWizardModule,
    shouldShowFirstTimeSetup,
    reducedOnboardingView,
    onboardingWaitingView,
    permissionsLoaded,
    isOnboardingWizardAutoSaveEnabled,
  };
};
